import { merge } from 'lodash-es';
import { createStorage } from '@aviata/storage';

/**
 * Shared storage key for analytics events state
 * This is the same key used by bloomreach events to store event properties
 */
const ANALYTICS_EVENTS_STATE_KEY = 'free-avia:bloomreach-events-state';

/**
 * Event names that can be stored in analytics events state
 * These correspond to bloomreach event names but are used generically
 */
export const AnalyticsEventName = {
  SEARCH_BUTTON_CLICK: 'search_button_click',
  SERP_PAGE_OPEN: 'serp_page_open',
  OFFER_PRICE_CLICK: 'offer_price_click',
  FARE_SELECT: 'ff_choose_click',
  REVENUE_PAGE_OPEN: 'revenue_page_open',
  SUCCESS_BOOKING: 'success_booking',
  PAYMENT_CLICK: 'pay_button_click',
} as const;

export type AnalyticsEventNameType =
  (typeof AnalyticsEventName)[keyof typeof AnalyticsEventName];

/**
 * Type for the events state stored in session storage
 */
export type AnalyticsEventsState = Record<
  AnalyticsEventNameType,
  Record<string, unknown> | undefined
>;

/**
 * Order of events in the analytics flow
 * Used to collect properties from all previous events up to a given event
 */
export const ANALYTICS_EVENTS_ORDER: readonly AnalyticsEventNameType[] = [
  AnalyticsEventName.SEARCH_BUTTON_CLICK,
  AnalyticsEventName.SERP_PAGE_OPEN,
  AnalyticsEventName.OFFER_PRICE_CLICK,
  AnalyticsEventName.FARE_SELECT,
  AnalyticsEventName.REVENUE_PAGE_OPEN,
  AnalyticsEventName.SUCCESS_BOOKING,
  AnalyticsEventName.PAYMENT_CLICK,
] as const;

/**
 * Gets the analytics events state from session storage
 * This is a generic utility that reads the same storage that bloomreach uses
 */
export const getAnalyticsEventsState = (): AnalyticsEventsState => {
  const storage = createStorage(sessionStorage);
  return (storage.parse(ANALYTICS_EVENTS_STATE_KEY) ??
    {}) as AnalyticsEventsState;
};

/**
 * Collects properties from all previous events up to (but not including) the given event
 * This is the main logic used by bloomreach to automatically collect properties from previous events
 * Based on the events order, it collects all events that occurred before the current event
 *
 * @param event - The event name to collect properties up to
 * @returns Merged properties from all previous events in the flow
 */
export const collectPropertiesUpToEvent = (
  event: AnalyticsEventNameType,
): Record<string, unknown> => {
  const state = getAnalyticsEventsState();
  const props: Record<string, unknown> = {};

  let currentEventIdx = 0;
  while (
    currentEventIdx < ANALYTICS_EVENTS_ORDER.length &&
    ANALYTICS_EVENTS_ORDER[currentEventIdx] !== event
  ) {
    const eventName = ANALYTICS_EVENTS_ORDER[currentEventIdx];
    const eventData = state[eventName];
    if (eventData) {
      merge(props, eventData);
    }
    currentEventIdx++;
  }

  return props;
};

/**
 * Collects properties from specific analytics events for use in other events
 * This allows events like booking_succeeded to collect data from previous events
 * (serp_page_opened, offer_price_click, revenue_page_open, fare_select)
 *
 * @param eventNames - Array of event names to collect properties from
 * @returns Merged properties from all specified events
 */
export const collectAnalyticsEventProperties = (
  eventNames: AnalyticsEventNameType[],
): Record<string, unknown> => {
  const state = getAnalyticsEventsState();
  const props: Record<string, unknown> = {};

  for (const eventName of eventNames) {
    const eventData = state[eventName];
    if (eventData) {
      merge(props, eventData);
    }
  }

  return props;
};

/**
 * Collects properties from previous analytics events for booking_succeeded event
 * This includes data from search_button_click, serp_page_opened, offer_price_click, revenue_page_open, and fare_select
 */
export const collectBookingEventProperties = (): Record<string, unknown> => {
  return collectAnalyticsEventProperties([
    AnalyticsEventName.SEARCH_BUTTON_CLICK,
    AnalyticsEventName.SERP_PAGE_OPEN,
    AnalyticsEventName.OFFER_PRICE_CLICK,
    AnalyticsEventName.REVENUE_PAGE_OPEN,
    AnalyticsEventName.FARE_SELECT,
  ]);
};
